import React, { useCallback, useState } from "react";
import { Form, Input, Button, message, Modal, Select, Spin, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import styled from "styled-components";
import { Trash } from "@phosphor-icons/react";
import { getCompany, editCompany, getCompanyBranches, deleteCompanyBranch, changePin, checkSlugAvailability, checkOnlineOrderingStatus } from "../../../service/client";
import { colors, spacing, sizes } from "../../../styles/theme";

interface CompanyDataModalProps {
  open: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

const StyledModal = styled(Modal)`
  .ant-modal {
    top: 50% !important;
    transform: translateY(-50%) !important;
    padding-bottom: 0 !important;
  }

  .ant-modal-content {
    display: flex;
    flex-direction: column;
    height: 90vh;
    max-height: 90vh;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-header {
    flex-shrink: 0;
    padding: 20px ${spacing.xl} 0;
  }

  .ant-modal-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
  }
`;

const TabsNav = styled.div`
  flex-shrink: 0;
  padding: ${spacing.lg} ${spacing.xl} 0;

  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing.xl};
  padding-bottom: 80px;
`;

const ModalFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  padding: ${spacing.lg} ${spacing.xl};
  background: ${colors.white};
  border-top: 1px solid ${colors.borderLight};
  border-radius: 0 0 ${sizes.borderRadiusSmall} ${sizes.borderRadiusSmall};
  z-index: 1;
`;

const BranchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BranchItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  background: ${colors.white};
`;

const BranchItemName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.textPrimary};
`;

const BranchItemAddress = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  margin-top: 2px;
`;

const DeleteBranchButton = styled(Button)`
  color: ${colors.error};
  &:hover {
    color: var(--color-error) !important;
  }
`;

const SlugPrefix = styled.span`
  color: ${colors.textSecondary};
  font-size: 14px;
  white-space: nowrap;
  user-select: none;
`;

const LockedNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: var(--color-background-secondary, #f5f5f5);
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: ${colors.textSecondary};
  line-height: 1.5;
`;

export const CompanyDataModal: React.FC<CompanyDataModalProps> = ({ open, onClose, isAdmin = false }) => {
  const { t } = useTranslation();
  const [basicForm] = Form.useForm();
  const [emailSmsForm] = Form.useForm();
  const [changePinForm] = Form.useForm();
  const [reservationsForm] = Form.useForm();
  const [integrationsForm] = Form.useForm();
  const [seoForm] = Form.useForm();
  const [activeTabKey, setActiveTabKey] = useState("basic");
  const queryClient = useQueryClient();

  const formMap: Record<string, any> = {
    basic: basicForm,
    "email-sms": emailSmsForm,
    pin: changePinForm,
    reservations: reservationsForm,
    integrations: integrationsForm,
    seo: seoForm,
  };

  const { data: company, isLoading } = useQuery(
    ["/company"],
    () => getCompany(),
    {
      onSuccess: (data) => {
        if (data) {
          basicForm.setFieldsValue({
            name: data.name,
            slug: data.slug || "",
            nip: data.nip,
            regon: data.regon || "",
            email: data.email,
            phoneNumber: data.phoneNumber,
            city: data.city || "",
            adress: data.adress || "",
          });
          emailSmsForm.setFieldsValue({
            emailFrom: data.emailFrom,
            smsFrom: data.smsFrom,
          });
          reservationsForm.setFieldsValue({
            serviceAvaliabilityIntervalMinutes: data.serviceAvaliabilityIntervalMinutes ?? 15,
          });
          integrationsForm.setFieldsValue({
            googleAnalyticsId: data.googleAnalyticsId || "",
            facebookPixelId: data.facebookPixelId || "",
          });
          seoForm.setFieldsValue({
            ogTitle: data.ogTitle || "",
            ogDescription: data.ogDescription || "",
            ogImage: data.ogImage || "",
          });
        }
      },
      enabled: open,
    }
  );

  const updateMutation = useMutation(
    async (values: any) => {
      if (!company?.id) throw new Error("Brak ID firmy");
      return await editCompany({ id: company.id, data: values });
    },
    {
      onSuccess: () => {
        message.success(t("companyDataModal.companyDataUpdated"));
        queryClient.invalidateQueries(["/company"]);
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.error || t("companyDataModal.updateError")
        );
      },
    }
  );

  const { data: agreementStatus } = useQuery(
    ["/online-ordering-status"],
    () => checkOnlineOrderingStatus(),
    { enabled: open }
  );

  const isAgreementSigned = agreementStatus?.status === "signed";

  const { data: branches } = useQuery(
    ["/company-branches"],
    () => getCompanyBranches(),
    { enabled: open }
  );

  const deleteBranchMutation = useMutation(deleteCompanyBranch, {
    onSuccess: () => {
      message.success(t("companyDataModal.branchDeleted"));
      queryClient.invalidateQueries(["/company-branches"]);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.error || t("companyDataModal.branchDeleteError"));
    },
  });

  const changePinMutation = useMutation(changePin, {
    onSuccess: () => {
      message.success(t("companyDataModal.pinChanged"));
      changePinForm.resetFields();
      onClose();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.error || t("companyDataModal.pinChangeError"));
    },
  });

  const handleSave = useCallback(async (form: any) => {
    try {
      const values = await form.validateFields();
      await updateMutation.mutateAsync(values);
      onClose();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  }, [updateMutation, onClose]);

  const handleFooterSave = useCallback(() => {
    const activeForm = formMap[activeTabKey];
    if (activeForm) {
      activeForm.submit();
    }
  }, [activeTabKey, formMap]);

  const tabItems = [
    { key: "basic", label: t("companyDataModal.tabBasic") },
    { key: "email-sms", label: t("companyDataModal.tabEmailSms") },
    { key: "pin", label: t("companyDataModal.tabPin") },
    { key: "branches", label: t("companyDataModal.tabBranches") },
    { key: "reservations", label: t("companyDataModal.tabReservations") },
    { key: "integrations", label: t("companyDataModal.tabIntegrations") },
    { key: "seo", label: t("companyDataModal.tabSeo") },
  ];

  const renderActiveTab = () => {
    switch (activeTabKey) {
      case "basic":
        return (
          <Form form={basicForm} layout="vertical" onFinish={async (values) => {
            try {
              const slug = values.slug?.trim().toLowerCase();
              if (slug) {
                const slugCheck = await checkSlugAvailability(slug);
                if (!slugCheck?.available) {
                  basicForm.setFields([
                    { name: "slug", errors: [slugCheck?.reason || t("companyDataModal.slugTaken")] },
                  ]);
                  return;
                }
              }
              await handleSave(basicForm);
            } catch (error) {
              console.error("Save failed:", error);
            }
          }}>
            <Form.Item
              name="slug"
              label={t("companyDataModal.menuLinkLabel")}
              rules={[
                { required: true, message: t("companyDataModal.menuLinkRequired") },
                { pattern: /^[a-z0-9-]+$/, message: t("companyDataModal.menuLinkPattern") },
                { min: 3, message: t("companyDataModal.menuLinkMin") },
                { max: 50, message: t("companyDataModal.menuLinkMax") },
              ]}
              normalize={(value: string) => value?.toLowerCase().replace(/\s/g, "-")}
            >
              <Input prefix={<SlugPrefix>proste.menu/</SlugPrefix>} placeholder={t("companyDataModal.menuLinkPlaceholder")} />
            </Form.Item>

            {isAgreementSigned && (
              <LockedNotice>
                <span style={{ fontSize: 16, flexShrink: 0 }}>🔒</span>
                <span>
                  {t("companyDataModal.lockedNotice")}
                </span>
              </LockedNotice>
            )}

            <Form.Item name="name" label={t("companyDataModal.companyNameLabel")} rules={[{ required: true, message: t("companyDataModal.required") }]}>
              <Input placeholder={t("companyDataModal.companyNamePlaceholder")} disabled={isAgreementSigned || isAdmin} />
            </Form.Item>
            <Form.Item name="nip" label="NIP" rules={[{ required: true, message: t("companyDataModal.required") }]}>
              <Input placeholder="NIP" disabled={isAgreementSigned || isAdmin} />
            </Form.Item>
            <Form.Item name="regon" label="REGON">
              <Input placeholder={t("companyDataModal.regonPlaceholder")} disabled={isAgreementSigned || isAdmin} />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, message: t("companyDataModal.required") }, { type: "email", message: t("companyDataModal.invalidEmail") }]}>
              <Input placeholder="Email" disabled={isAgreementSigned} />
            </Form.Item>
            <Form.Item name="phoneNumber" label={t("companyDataModal.phoneLabel")} rules={[{ required: true, message: t("companyDataModal.required") }]}>
              <Input placeholder={t("companyDataModal.phonePlaceholder")} disabled={isAgreementSigned} />
            </Form.Item>
            <Form.Item name="city" label={t("companyDataModal.cityLabel")}>
              <Input placeholder={t("companyDataModal.cityPlaceholder")} disabled={isAgreementSigned || isAdmin} />
            </Form.Item>
            <Form.Item name="adress" label={t("companyDataModal.streetLabel")}>
              <Input placeholder={t("companyDataModal.streetPlaceholder")} disabled={isAgreementSigned || isAdmin} />
            </Form.Item>
          </Form>
        );
      case "email-sms":
        return (
          <Form form={emailSmsForm} layout="vertical" onFinish={() => handleSave(emailSmsForm)} initialValues={{ emailFrom: "proste.menu" }}>
            <Form.Item name="emailFrom" label={t("companyDataModal.emailSenderLabel")} rules={[{ required: true, message: t("companyDataModal.required") }]}>
              <Input placeholder="proste.menu" />
            </Form.Item>
            <Form.Item
              label={t("companyDataModal.smsSenderLabel")}
              help={t("companyDataModal.smsSenderHelp")}
            >
              <Input value="proste.menu" disabled />
            </Form.Item>
          </Form>
        );
      case "pin":
        return (
          <Form
            form={changePinForm}
            layout="vertical"
            onFinish={async (values: any) => {
              try {
                await changePinMutation.mutateAsync({
                  currentPin: values.currentPin,
                  newPin: values.newPin,
                });
              } catch { }
            }}
          >
            <Form.Item
              name="currentPin"
              label={t("companyDataModal.currentPinLabel")}
              rules={[{ required: true, message: t("companyDataModal.required") }]}
            >
              <Input.Password placeholder={t("companyDataModal.currentPinLabel")} />
            </Form.Item>
            <Form.Item
              name="newPin"
              label={t("companyDataModal.newPinLabel")}
              rules={[{ required: true, message: t("companyDataModal.required") }]}
            >
              <Input.Password placeholder={t("companyDataModal.newPinLabel")} />
            </Form.Item>
            <Form.Item
              name="confirmPin"
              label={t("companyDataModal.confirmPinLabel")}
              dependencies={["newPin"]}
              rules={[
                { required: true, message: t("companyDataModal.required") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPin") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("companyDataModal.pinsDoNotMatch")));
                  },
                }),
              ]}
            >
              <Input.Password placeholder={t("companyDataModal.confirmPinLabel")} />
            </Form.Item>
          </Form>
        );
      case "branches":
        return (
          <BranchList>
            {branches?.map((branch: any) => (
              <BranchItem key={branch.id}>
                <div>
                  <BranchItemName>{branch.name}</BranchItemName>
                  {branch.formattedAddress && (
                    <BranchItemAddress>{branch.formattedAddress}</BranchItemAddress>
                  )}
                </div>
                {isAdmin && (
                  <DeleteBranchButton
                    type="text"
                    icon={<Trash size={18} />}
                    loading={deleteBranchMutation.isLoading}
                    onClick={() => {
                      Modal.confirm({
                        title: t("companyDataModal.deleteBranchTitle"),
                        content: t("companyDataModal.deleteBranchContent", { name: branch.name }),
                        okText: t("companyDataModal.delete"),
                        okButtonProps: { danger: true },
                        onOk: () => deleteBranchMutation.mutateAsync(branch.id),
                      });
                    }}
                  />
                )}
              </BranchItem>
            ))}
          </BranchList>
        );
      case "reservations":
        return (
          <Form form={reservationsForm} layout="vertical" onFinish={() => handleSave(reservationsForm)}>
            <Form.Item
              name="serviceAvaliabilityIntervalMinutes"
              label={t("companyDataModal.reservationIntervalLabel")}
              help={t("companyDataModal.reservationIntervalHelp")}
            >
              <Select
                options={[
                  { label: t("companyDataModal.intervalDisable"), value: 0 },
                  { label: t("companyDataModal.interval5"), value: 5 },
                  { label: t("companyDataModal.interval10"), value: 10 },
                  { label: t("companyDataModal.interval15"), value: 15 },
                  { label: t("companyDataModal.interval30"), value: 30 },
                  { label: t("companyDataModal.interval60"), value: 60 },
                ]}
              />
            </Form.Item>
          </Form>
        );
      case "integrations":
        return (
          <Form form={integrationsForm} layout="vertical" onFinish={() => handleSave(integrationsForm)}>
            <Form.Item
              name="googleAnalyticsId"
              label="Google Analytics ID"
              help={t("companyDataModal.googleAnalyticsHelp")}
            >
              <Input placeholder="G-XXXXXXXXXX" />
            </Form.Item>
            <Form.Item
              name="facebookPixelId"
              label="Facebook Pixel ID"
              help={t("companyDataModal.facebookPixelHelp")}
            >
              <Input placeholder="123456789012345" />
            </Form.Item>
          </Form>
        );
      case "seo":
        return (
          <Form form={seoForm} layout="vertical" onFinish={() => handleSave(seoForm)}>
            <Form.Item
              name="ogTitle"
              label={t("companyDataModal.ogTitleLabel")}
              help={t("companyDataModal.ogTitleHelp")}
            >
              <Input placeholder={t("companyDataModal.ogTitlePlaceholder")} />
            </Form.Item>
            <Form.Item
              name="ogDescription"
              label={t("companyDataModal.ogDescriptionLabel")}
              help={t("companyDataModal.ogDescriptionHelp")}
            >
              <Input.TextArea placeholder={t("companyDataModal.ogDescriptionPlaceholder")} rows={3} />
            </Form.Item>
            <Form.Item
              name="ogImage"
              label={t("companyDataModal.ogImageLabel")}
              help={t("companyDataModal.ogImageHelp")}
            >
              <Input placeholder="https://example.com/og-image.jpg" />
            </Form.Item>
          </Form>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <StyledModal open={open} onCancel={onClose} title={t("companyDataModal.modalTitle")} footer={null} width={600}>
        <Spin />
      </StyledModal>
    );
  }

  return (
    <StyledModal
      title={t("companyDataModal.modalTitle")}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <TabsNav>
        <Tabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          items={tabItems}
          size="small"
          destroyInactiveTabPane={false}
        />
      </TabsNav>
      <ScrollArea>
        {renderActiveTab()}
      </ScrollArea>
      <ModalFooter>
        {activeTabKey !== "branches" && (
          <Button
            type="primary"
            onClick={handleFooterSave}
            loading={activeTabKey === "pin" ? changePinMutation.isLoading : updateMutation.isLoading}
          >
            {t("companyDataModal.save")}
          </Button>
        )}
      </ModalFooter>
    </StyledModal>
  );
};
